import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  message
} from "antd";
import { useForm } from "antd/lib/form/Form";
import {
  dummyColumnDef,
  IntegrationNameCellRenderer,
  integrationTypeCellRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { GridLoadingIndicator } from "components/widgets";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  authorizeOpcIntegration,
  getOAuthUrl
} from "services/integration-service";
import { getUser } from "services/auth";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useIntegrationAuthProcess } from "hooks/integration-hooks";
import SubscriptionIntegrationListMessage from "components/widgets/subscription-integration-list-message";
import { useApolloClient } from "@apollo/client";
import { OAUTH_OUTPUT } from "services/graphQL/ciq-queries";
import { getGQL } from "utils/utils";

import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import {
  addedByColumnDef,
  accountNameHeaderComponentParams
} from "pages/subscription-settings/common-components";
import {
  ErrorMessages,
  IntegrationAuthProcessStatuses,
  IntegrationSystemNameMap,
  OPC_DEFAULT_ENDPOINT_URL,
  OPC_INTEGRATION_SYSTEM,
  SidepanelStyles
} from "../../../constants";
import {
  IntegrationType,
  SCHEDULE_SOURCE_SYSTEMS,
  SUBMITTAL_SOURCE_SYSTEMS,
  buildOpcConfigureAuthData,
  canOpenIntegrationDetails,
  mergeOpcIntegrationsByAddedBy
} from "./utils";
import { IntegrationsTabSharedContext } from "./integrations-tab-context";
import OpcSetupInstructions from "./opc/opc-setup-instructions";

function IntegrationsList() {
  const { subscriptionRole } = useContext(SubscriptionContext);
  const { dispatch, sharedState } = useContext(IntegrationsTabSharedContext);
  const apolloClient = useApolloClient();

  const gridRef = useRef<AgGridReact>(null);
  const [form] = useForm();

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  const [addingIntegration, setAddingIntegration] = useState(false);

  const { initAuthProcess, resetAuthProcess } = useIntegrationAuthProcess();

  const [openAddIntegrationPanel, setOpenAddIntegrationPanel] = useState(false);
  const [addPanelStep, setAddPanelStep] = useState<"source" | "credentials">(
    "source"
  );
  // Keep OPC fields across the credentials step — source Form.Items unmount.
  const [opcAuthDraft, setOpcAuthDraft] = useState<{
    integrationType?: string | number;
    integrationName?: string;
    endpoint_url: string;
  } | null>(null);

  const [endpointFieldsDisabled, setEndpointFieldsDisabled] = useState(true);

  const canAddIntegration =
    subscriptionRole === RoleSubscriptionEnum.subscription_admin;

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      filter: "agSetColumnFilter",
      autoHeight: false,
      resizable: true,
      flex: 1,
      minWidth: 100,
      comparator: comparatorWithEmptyAtBottom,
      cellClass: "ciq-cell-v-center"
    };
  }, []);

  const opcSystemApiLoginUrl = useMemo(() => {
    return (
      sharedState?.systemIntegrationConfigs?.find(
        (config: any) => config.system === OPC_INTEGRATION_SYSTEM
      )?.integration_config_properties?.OPC_API_LOGIN_URL ||
      OPC_DEFAULT_ENDPOINT_URL
    );
  }, [sharedState?.systemIntegrationConfigs]);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        headerName: "Integration Name",
        headerTooltip: "Integration Name",
        valueGetter: ({ data }: any) => data?.properties?.INTEGRATION_NAME,
        cellRenderer: IntegrationNameCellRenderer
      },
      {
        headerName: "Source Application",
        headerTooltip: "Source Application",
        field: "integration.system",
        filterParams: {
          valueFormatter: (params: any) =>
            IntegrationSystemNameMap[params.value] || params.value
        },
        cellRenderer: integrationTypeCellRenderer
      },
      {
        headerName: "Account Name",
        headerComponentParams: accountNameHeaderComponentParams,
        valueGetter: ({ data }: any) => {
          return (
            data.properties.PROCORE_COMPANY_NAME ||
            data.properties.AUTODESK_ACCOUNT_NAME ||
            data.properties.OPC_WORKSPACE_NAME
          );
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "End Point",
        headerTooltip: "End Point",
        minWidth: 250,
        valueGetter: ({ data }: any) => {
          if (data?.integration?.system === OPC_INTEGRATION_SYSTEM) {
            return (
              data.properties?.OPC_API_LOGIN_URL ||
              data.systemIntgConfigProperties?.OPC_API_LOGIN_URL ||
              opcSystemApiLoginUrl
            );
          }
          return (
            data.properties.PROCORE_ROOT_URL || data.properties.ACC_ROOT_URL
          );
        },
        cellRenderer: truncatedTextCellRenderer
      },
      addedByColumnDef
    ];
  }, [opcSystemApiLoginUrl]);

  const availableIntegrationOptions = useMemo(() => {
    if (!sharedState?.availableIntegrations) return [];

    const activeSubSourceType = ["AUTODESK", "PROCORE", "OPC"];

    const opts = sharedState.availableIntegrations
      .filter((opt: any) => activeSubSourceType.includes(opt?.system))
      .map((availableIntegration: any) => {
        return {
          value: availableIntegration.id,
          label: IntegrationSystemNameMap[availableIntegration.system],
          system: availableIntegration.system
        };
      });

    // Always surface OPC under Schedules even if the API omits it.
    if (!opts.some((o: any) => o.system === OPC_INTEGRATION_SYSTEM)) {
      const opcFromApi = sharedState.availableIntegrations.find(
        (i: any) => i.system === OPC_INTEGRATION_SYSTEM
      );
      opts.push({
        value: opcFromApi?.id ?? IntegrationType.OPC,
        label: IntegrationSystemNameMap[OPC_INTEGRATION_SYSTEM],
        system: OPC_INTEGRATION_SYSTEM
      });
    }

    return [
      {
        label: "Submittals",
        options: opts.filter((o: any) =>
          SUBMITTAL_SOURCE_SYSTEMS.includes(o.system)
        )
      },
      {
        label: "Schedules",
        options: opts.filter((o: any) =>
          SCHEDULE_SOURCE_SYSTEMS.includes(o.system)
        )
      }
    ].filter((g) => g.options.length > 0);
  }, [sharedState?.availableIntegrations]);

  const selectedIntegrationType = Form.useWatch("integrationType", form);

  const selectedIntegration = useMemo(() => {
    if (
      selectedIntegrationType === undefined ||
      selectedIntegrationType === null
    ) {
      return undefined;
    }
    return (
      sharedState?.availableIntegrations?.find(
        (i: any) => String(i.id) === String(selectedIntegrationType)
      ) ||
      availableIntegrationOptions
        .flatMap((g: any) => g.options)
        .find((o: any) => String(o.value) === String(selectedIntegrationType))
    );
  }, [
    sharedState?.availableIntegrations,
    selectedIntegrationType,
    availableIntegrationOptions
  ]);

  const selectedSystem =
    selectedIntegration?.system ??
    (String(selectedIntegrationType) === String(IntegrationType.OPC)
      ? OPC_INTEGRATION_SYSTEM
      : undefined);

  const isOpcSelected = selectedSystem === OPC_INTEGRATION_SYSTEM;
  const isProcoreSelected = selectedSystem === "PROCORE";
  const isAutodeskSelected = selectedSystem === "AUTODESK";

  const foundProcoreConfig = useMemo(() => {
    return sharedState?.systemIntegrationConfigs?.find(
      (config: any) =>
        config.system === IntegrationType[IntegrationType.PROCORE]
    );
  }, [sharedState?.systemIntegrationConfigs]);

  const resetEndpointFields = () => {
    form.setFieldsValue({
      root_url:
        foundProcoreConfig?.integration_config_properties?.PROCORE_ROOT_URL,
      login_url:
        foundProcoreConfig?.integration_config_properties?.PROCORE_LOGIN_URL
    });
  };

  useEffect(() => {
    if (isProcoreSelected) {
      resetEndpointFields();
    }
    if (isOpcSelected) {
      form.setFieldsValue({
        endpoint_url: opcSystemApiLoginUrl
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedIntegrationType, foundProcoreConfig, opcSystemApiLoginUrl]);

  const closeAddPanel = () => {
    resetAuthProcess();
    setOpenAddIntegrationPanel(false);
    setAddPanelStep("source");
    setOpcAuthDraft(null);
    form.resetFields();
  };

  const onFormSubmit = async (values: any) => {
    async function submitOpcCredentials() {
      setAddingIntegration(true);
      const endpointUrl =
        opcAuthDraft?.endpoint_url || OPC_DEFAULT_ENDPOINT_URL;
      const integrationName = opcAuthDraft?.integrationName;

      const authResponse = await authorizeOpcIntegration({
        username: values.username,
        password: values.password,
        endpoint_url: endpointUrl
      });

      if (!authResponse.success) {
        setAddingIntegration(false);
        message.error(authResponse.error || "Authorization failed");
        return;
      }

      const operationId =
        authResponse.data?.id ||
        authResponse.data?.opc_auth_operation?.id ||
        authResponse.data?.auth_operation?.id;

      let authDataPayload: any = null;
      if (operationId) {
        try {
          const { query } = getGQL(OAUTH_OUTPUT);
          const result = await apolloClient.query({
            query,
            variables: { id: operationId },
            fetchPolicy: "network-only"
          });
          const operation = result.data?.oauth_operations?.[0];
          if (operation) {
            authDataPayload = { ...operation, id: operationId };
          }
        } catch {
          // Fall through to REST-shaped authData below.
        }
      }

      authDataPayload = buildOpcConfigureAuthData(
        authDataPayload || authResponse.data,
        values.username,
        getUser()
      );

      setAddingIntegration(false);
      setOpenAddIntegrationPanel(false);
      setAddPanelStep("source");
      setOpcAuthDraft(null);
      form.resetFields();
      dispatch({
        type: "SET_SHARED_STATE",
        payload: {
          activeView: "configure-initial",
          system: OPC_INTEGRATION_SYSTEM,
          authData: authDataPayload,
          additionalData: {
            newIntegrationName: integrationName,
            endpoint_url: endpointUrl
          }
        }
      });
    }

    if (addPanelStep === "credentials") {
      await submitOpcCredentials();
      return;
    }

    if (isOpcSelected) {
      setOpcAuthDraft({
        integrationType: values.integrationType,
        integrationName: values.integrationName,
        endpoint_url: values.endpoint_url || OPC_DEFAULT_ENDPOINT_URL
      });
      setAddPanelStep("credentials");
      return;
    }

    setAddingIntegration(true);
    let payload = {};
    if (isProcoreSelected) {
      payload = {
        login_url: values.login_url,
        root_url: values.root_url
      };
    }

    const oauthSystem =
      selectedSystem ||
      (typeof values.integrationType === "number"
        ? IntegrationType[values.integrationType]
        : undefined);

    if (!oauthSystem) {
      setAddingIntegration(false);
      message.error("Select a valid source application.");
      return;
    }

    const OAuthURLResponse = await getOAuthUrl(oauthSystem, payload);
    setAddingIntegration(false);
    if (OAuthURLResponse.success) {
      const authResponse: any = await initAuthProcess(OAuthURLResponse.data);
      if (
        authResponse?.oauth_status?.status ===
        IntegrationAuthProcessStatuses.PROCESSED
      ) {
        setOpenAddIntegrationPanel(false);
        dispatch({
          type: "SET_SHARED_STATE",
          payload: {
            activeView: "configure-initial",
            system: authResponse.source_type.toUpperCase(),
            additionalData: {
              newIntegrationName: form.getFieldValue("integrationName")
            }
          }
        });
      } else {
        const error = authResponse?.output?.error || "Authorization failed";
        message.error(error);
      }
    }
  };

  const onManageIntegrationClick = (data: any) => {
    // A merged OPC row carries display-only name/workspace values; the details
    // view gets the real config it was built from.
    const config = data?.opcMergedConfigs?.[0] ?? data;
    dispatch({
      type: "SET_SHARED_STATE",
      payload: {
        activeView: "details",
        system: config.integration.system,
        additionalData: config
      }
    });
  };

  const subscriptionIntegrations = useMemo(() => {
    // File-based Autodesk/BIM360 configs are hidden here; OPC is also created
    // via method_type/file but must still appear in the subscription list.
    return mergeOpcIntegrationsByAddedBy(
      sharedState?.integrationConfigs?.filter((config: any) => {
        if (config?.integration?.system === OPC_INTEGRATION_SYSTEM) {
          return true;
        }
        return (
          config?.systemIntgConfigProperties?.INTEGRATION_METHOD !== "file"
        );
      })
    );
  }, [sharedState?.integrationConfigs]);

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  return (
    <div>
      <div>
        <CiqLogPageHeader
          entityName="Integrations"
          titleParam={{
            title: "Integrations",
            totalCount: subscriptionIntegrations?.length || 0,
            filterStats: statsData
          }}
          items={[
            <Button
              key="add-integration"
              type="primary"
              onClick={() => {
                setOpenAddIntegrationPanel(true);
              }}
              disabled={!canAddIntegration || addingIntegration}
              title={
                !canAddIntegration ? ErrorMessages.PermissionNotGranted : ""
              }
              data-testid="add-integration-button"
            >
              Add Integration
            </Button>
          ]}
        />
        <div className="h-[500px] ag-theme-alpine relative">
          {sharedState?.integrationConfigs &&
            !sharedState?.integrationConfigs.length && (
              <SubscriptionIntegrationListMessage
                onConfigureClick={() => {
                  setOpenAddIntegrationPanel(true);
                }}
              />
            )}
          <AgGridReact<any>
            ref={gridRef}
            onFirstDataRendered={() => {
              gridRef?.current?.api?.sizeColumnsToFit();
              gridRef?.current?.api?.resetRowHeights();
            }}
            rowData={subscriptionIntegrations}
            columnDefs={columnDefs} // Column Defs for Columns
            defaultColDef={defaultColDef} // Default Column Properties
            rowHeight={47}
            animateRows={false} // Optional - set to 'true' to have rows animate when sorted
            loadingOverlayComponent={GridLoadingIndicator}
            suppressDragLeaveHidesColumns
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            tooltipShowMode="whenTruncated"
            onFilterChanged={onFiltersApplied}
            suppressContextMenu
            suppressRowDrag
            suppressMovableColumns
            context={{
              onManageIntegrationClick,
              subscriptionRole
            }}
            getRowClass={(params) =>
              canOpenIntegrationDetails(params.data)
                ? "integration-details-clickable-row"
                : undefined
            }
            onRowClicked={(event) => {
              if (canOpenIntegrationDetails(event.data)) {
                onManageIntegrationClick(event.data);
              }
            }}
            suppressNoRowsOverlay
          />
        </div>
      </div>

      <Modal
        className="custom-drawer"
        title="Add Integration"
        width={420}
        style={SidepanelStyles.style}
        styles={{
          body: {
            height: "calc(100vh - 57px)",
            padding: "10px 0px 10px 0px"
          }
        }}
        footer={null}
        open={openAddIntegrationPanel}
        onCancel={closeAddPanel}
        destroyOnHidden
        mask={{ closable: false }}
        keyboard={false}
      >
        <div className="px-3 h-full">
          <Form
            form={form}
            layout="vertical"
            className="h-full"
            onFinish={onFormSubmit}
            disabled={addingIntegration}
          >
            <div className="h-full flex flex-col">
              <div className="grow">
                {addPanelStep === "source" && (
                  <>
                    <div>
                      <Form.Item
                        name="integrationType"
                        label="Source Application"
                        required
                        rules={[
                          {
                            required: true,
                            message: "Select source  application"
                          }
                        ]}
                      >
                        <Select
                          options={availableIntegrationOptions}
                          placeholder="Select Source Application"
                          optionLabelProp="label"
                          classNames={{
                            popup: {
                              root: "integration-source-select-dropdown"
                            }
                          }}
                        />
                      </Form.Item>
                      {isAutodeskSelected && (
                        <div className="pb-2">
                          <div className="mt-2 mb-1 text-xs font-semibold">
                            Install the CIQ App in your Autodesk Account before
                            proceeding with the next steps.
                          </div>
                        </div>
                      )}
                      {isProcoreSelected && (
                        <div className="pb-2">
                          <div className="mt-2 mb-1 text-xs font-semibold">
                            Install the CIQ app in your Procore Company before
                            proceeding with the next steps.
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <Form.Item
                        name="integrationName"
                        label="Integration Name"
                        required
                        rules={[
                          {
                            required: true,
                            message: "Enter Integration Name"
                          }
                        ]}
                      >
                        <Input placeholder="Enter Integration Name" />
                      </Form.Item>
                    </div>
                    {isProcoreSelected && (
                      <div>
                        <Divider className="mx-0 mt-1 mb-4" />

                        <div>
                          <Form.Item
                            name="root_url"
                            label={
                              <div className="flex items-center">
                                <span>Endpoint URL</span>
                                <Popover
                                  placement="right"
                                  content={
                                    <div className="text-sm font-medium max-w-[200px]">
                                      Edit this endpoint if your data is not
                                      stored in the standard Procore zones.
                                    </div>
                                  }
                                >
                                  <InfoCircleOutlined className="px-0.5 -mt-0.5" />
                                </Popover>
                              </div>
                            }
                            required
                            rules={[
                              {
                                required: true,
                                message: "Enter the endpoint URL"
                              }
                            ]}
                          >
                            <Input disabled={endpointFieldsDisabled} />
                          </Form.Item>
                        </div>

                        <div>
                          <Form.Item
                            name="login_url"
                            label={
                              <div className="flex items-center">
                                <span>LOGIN URL</span>
                                <Popover
                                  placement="right"
                                  content={
                                    <div className="text-sm font-medium max-w-[200px]">
                                      Edit this endpoint if your user data is
                                      not stored in the standard Procore zones.{" "}
                                      <br />{" "}
                                      <a
                                        target="_blank"
                                        href="https://developers.procore.com/reference/rest/v1/docs/tutorial-mpz"
                                        rel="noreferrer"
                                      >
                                        Refer docs
                                      </a>{" "}
                                      for details.
                                    </div>
                                  }
                                >
                                  <InfoCircleOutlined className="px-0.5 -mt-0.5" />
                                </Popover>
                              </div>
                            }
                            required
                            rules={[
                              {
                                required: true,
                                message: "Enter the login URL"
                              }
                            ]}
                          >
                            <Input disabled={endpointFieldsDisabled} />
                          </Form.Item>
                        </div>
                        <div className="flex justify-end">
                          <Button
                            size="middle"
                            onClick={() => {
                              if (endpointFieldsDisabled) {
                                setEndpointFieldsDisabled(false);
                              } else {
                                resetEndpointFields();
                                setEndpointFieldsDisabled(true);
                              }
                            }}
                          >
                            {endpointFieldsDisabled ? "Edit" : "Cancel"}
                          </Button>
                        </div>
                      </div>
                    )}
                    {isOpcSelected && (
                      <div>
                        <Divider className="mx-0 mt-1 mb-4" />
                        <Form.Item
                          name="endpoint_url"
                          label="Endpoint URL"
                          required
                          rules={[
                            {
                              required: true,
                              message: "Enter the endpoint URL"
                            }
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      </div>
                    )}
                  </>
                )}
                {addPanelStep === "credentials" && (
                  <div>
                    <p className="mb-3 text-xs font-semibold text-black text-opacity-80">
                      Enter your Oracle Primavera Cloud credentials to
                      authorize.
                    </p>
                    <Form.Item
                      name="username"
                      label="User Name"
                      required
                      rules={[{ required: true, message: "Enter User Name" }]}
                    >
                      <Input placeholder="Enter User Name" autoComplete="off" />
                    </Form.Item>
                    <Form.Item
                      name="password"
                      label="Password"
                      required
                      rules={[{ required: true, message: "Enter Password" }]}
                    >
                      <Input.Password
                        placeholder="Enter Password"
                        autoComplete="off"
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
              <div>
                {addPanelStep === "source" && isAutodeskSelected && (
                  <div className="mt-2 mb-1 text-xs font-semibold">
                    <span className="font-semibold mb-1">
                      The next step is to login to Autodesk.
                    </span>
                    <br />
                    <span className="font-semibold mb-1">
                      To login, use the credentials of an Autodesk Forma Account
                      Admin who is part of at least one Autodesk® Project.
                    </span>
                    <br />
                    <span className="font-semibold mb-1">
                      Read{" "}
                      <a
                        href="https://apps.autodesk.com/BIM360/en/Detail/HelpDoc?appId=4545831120489722584&appLang=en&os=Web"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Help Document
                      </a>{" "}
                      for more details.
                    </span>
                  </div>
                )}
                {addPanelStep === "source" && isProcoreSelected && (
                  <div className="p-1 text-xs">
                    <span className="font-semibold mb-1">
                      The next step is to login to Procore.
                    </span>
                    <ul className="m-0 pl-4">
                      <li>
                        If you are already logged into Procore in another tab,
                        you will not need to log in again after clicking Next.
                      </li>
                      <li>
                        If you want to use a different credential, please log
                        out of Procore before clicking Next.
                      </li>
                    </ul>
                  </div>
                )}
                {addPanelStep === "source" && isOpcSelected && (
                  <div className="mt-2 mb-1">
                    <OpcSetupInstructions />
                  </div>
                )}
                <Divider className="mx-0 mt-1 mb-4" />
                <div className="flex justify-end space-x-4">
                  {addPanelStep === "credentials" ? (
                    <Button
                      className="px-5"
                      onClick={() => {
                        setAddPanelStep("source");
                        if (opcAuthDraft) {
                          form.setFieldsValue({
                            integrationType: opcAuthDraft.integrationType,
                            integrationName: opcAuthDraft.integrationName,
                            endpoint_url: opcAuthDraft.endpoint_url
                          });
                        }
                      }}
                      disabled={addingIntegration}
                    >
                      Back
                    </Button>
                  ) : (
                    <Button
                      htmlType="reset"
                      className="px-5"
                      onClick={closeAddPanel}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="px-5"
                    disabled={addingIntegration}
                    loading={addingIntegration}
                  >
                    {addPanelStep === "credentials" ? "Authorize" : "Next"}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
}

export default IntegrationsList;
